import React, { useCallback, useRef, useState } from 'react';

import type { UUID } from '@aline/common';
import { useApolloClient } from '@apollo/client/react';
import { ButtonBase, IconButton, InputBase, Typography } from '@mui/material';
import { CheckCircle2, Circle, Plus, Trash2 } from 'lucide-react';
import { AlineAvatar, AlineAvatarGroup, AlineTooltip } from 'src/components';
import { ConfirmationDialog } from 'src/components/generic/ConfirmationDialog';
import { Apollo } from 'src/gql/generated';
import { getPermissionAssigneeDisplayDetails, getPermissionUser } from 'src/helpers';
import { useCreateTask } from 'src/hooks';
import { useDeleteTask } from 'src/hooks/tasks';
import { makeStyles } from 'tss-react/mui';

import { TaskReviewStatusPill } from './TaskStatusPill';

const MAX_AVATARS = 3;

const useStyles = makeStyles<void, 'deleteButton'>()((theme, _params, classes) => ({
  // Revealed on hover or keyboard focus.
  deleteButton: {
    opacity: 0,
    transition: theme.transitions.create('opacity', { duration: 120 }),
  },
  draftInput: {
    flex: 1,
    fontSize: 14,
    minWidth: 0,
    padding: 0,
  },
  empty: {
    color: theme.appColors.iconsTypeMuted,
    fontSize: 14,
    padding: theme.spacing(1, 0),
    textAlign: 'center',
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
  },
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  row: {
    alignItems: 'center',
    borderRadius: 6,
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'flex-start',
    padding: theme.spacing(0.5, 0.5),
    textAlign: 'left',
    width: '100%',
  },
  rowButton: {
    flex: 1,
    minWidth: 0,
  },
  rowHover: {
    '&:hover, &:focus-within': {
      backgroundColor: theme.palette.action.hover,
    },
    [`&:hover .${classes.deleteButton}, &:focus-within .${classes.deleteButton}`]: {
      opacity: 1,
    },
    alignItems: 'center',
    borderRadius: 6,
    display: 'flex',
    gap: theme.spacing(0.5),
    paddingRight: theme.spacing(0.5),
    transition: theme.transitions.create('background-color', {
      duration: theme.transitions.duration.shortest,
    }),
  },
  sectionLabel: {
    color: theme.appColors.iconsType,
    fontSize: 14,
    fontWeight: 600,
  },
  title: {
    flex: 1,
    fontSize: 14,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));

type Subtask = Apollo.TaskReviewFieldsFragment['subtasks'][number];

interface TaskSubtaskListProps {
  // Adding and deleting sub-tasks.
  canEdit: boolean;
  onOpenTask: (taskId: UUID) => void;
  parentTaskId: UUID;
  phaseId: UUID;
  subtasks: readonly Subtask[];
  workflowId: UUID;
  /** Replaces the "Sub-tasks" label, e.g. with the task page's card title. */
  heading?: React.ReactNode;
}

export const TaskSubtaskList = React.memo(function TaskSubtaskList({
  canEdit,
  heading,
  onOpenTask,
  parentTaskId,
  phaseId,
  subtasks,
  workflowId,
}: TaskSubtaskListProps) {
  const { classes } = useStyles();
  const [isAdding, setIsAdding] = useState(false);

  if (!subtasks.length && !canEdit) return null;

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        {heading ?? <span className={classes.sectionLabel}>Sub-tasks</span>}
        {canEdit && (
          <AlineTooltip arrow placement="top" title="Add sub-task">
            <span>
              <IconButton
                aria-label="Add sub-task"
                disabled={isAdding}
                size="small"
                onClick={() => setIsAdding(true)}
              >
                <Plus size={16} />
              </IconButton>
            </span>
          </AlineTooltip>
        )}
      </div>
      <div className={classes.list}>
        {!subtasks.length && !isAdding && (
          <Typography className={classes.empty} variant="body2">
            No sub-tasks
          </Typography>
        )}
        {subtasks.map(subtask => (
          <SubtaskRow
            key={subtask.id}
            canDelete={canEdit}
            phaseId={phaseId}
            subtask={subtask}
            workflowId={workflowId}
            onOpenTask={onOpenTask}
          />
        ))}
        {isAdding && (
          <AddSubtaskInput
            parentTaskId={parentTaskId}
            phaseId={phaseId}
            workflowId={workflowId}
            onDone={() => setIsAdding(false)}
          />
        )}
      </div>
    </div>
  );
});

interface SubtaskRowProps {
  canDelete: boolean;
  onOpenTask: (taskId: UUID) => void;
  phaseId: UUID;
  subtask: Subtask;
  workflowId: UUID;
}

/** Opens the sub-task; a trash button shows on hover, left of the assignees on the right. */
function SubtaskRow({
  canDelete,
  onOpenTask,
  phaseId,
  subtask,
  workflowId,
}: SubtaskRowProps): JSX.Element {
  const { classes, cx } = useStyles();
  const client = useApolloClient();
  const { deleteTask } = useDeleteTask();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const title = subtask.title || 'Untitled';

  const handleDelete = useCallback(async () => {
    setIsConfirmOpen(false);
    const { error } = await deleteTask(subtask.id, workflowId, phaseId);
    if (!error) await client.refetchQueries({ include: [Apollo.GetTaskWithCommentsDocument] });
  }, [client, deleteTask, phaseId, subtask.id, workflowId]);

  return (
    <div className={classes.rowHover}>
      <ButtonBase
        className={cx(classes.row, classes.rowButton)}
        onClick={() => onOpenTask(subtask.id)}
      >
        <SubtaskIcon isComplete={Boolean(subtask.completedAt)} />
        <Typography className={classes.title}>{title}</Typography>
        <TaskReviewStatusPill status={subtask.status} />
      </ButtonBase>
      {canDelete && (
        <AlineTooltip arrow placement="top" title="Delete sub-task">
          <IconButton
            aria-label={`Delete ${title}`}
            className={classes.deleteButton}
            size="small"
            onClick={() => setIsConfirmOpen(true)}
          >
            <Trash2 size={16} />
          </IconButton>
        </AlineTooltip>
      )}
      <AlineAvatarGroup max={MAX_AVATARS} styles={{ size: 'sm' }}>
        {subtask.assignments.map(({ permission }) => {
          const displayDetails = getPermissionAssigneeDisplayDetails(permission);
          return (
            <AlineTooltip key={permission.id} arrow placement="top" title={displayDetails.primary}>
              <AlineAvatar
                avatarInfo={getPermissionUser(permission) ?? permission.role}
                styles={{ backgroundColor: displayDetails.avatarColor, size: 'sm' }}
              />
            </AlineTooltip>
          );
        })}
      </AlineAvatarGroup>
      <ConfirmationDialog
        message={`Delete sub-task "${title}"? This action cannot be undone.`}
        open={isConfirmOpen}
        title="Delete Sub-task"
        onClickPrimary={() => void handleDelete()}
        onClose={() => setIsConfirmOpen(false)}
      />
    </div>
  );
}

function SubtaskIcon({ isComplete }: { isComplete: boolean }): JSX.Element {
  if (isComplete) return <CheckCircle2 color="rgb(30, 128, 72)" size={16} />;
  return <Circle color="rgb(84, 96, 110)" size={16} />;
}

interface AddSubtaskInputProps {
  onDone: () => void;
  parentTaskId: UUID;
  phaseId: UUID;
  workflowId: UUID;
}

/** A draft row shaped like the sub-task it becomes. Enter or blur saves it; Escape discards it. */
function AddSubtaskInput({ onDone, parentTaskId, phaseId, workflowId }: AddSubtaskInputProps) {
  const { classes } = useStyles();
  const [title, setTitle] = useState('');
  // Enter, Escape and the blur that follows them must settle the draft once.
  const isSettledRef = useRef(false);
  const { createTask, loading } = useCreateTask();

  const settle = async (shouldSave: boolean) => {
    if (isSettledRef.current) return;
    isSettledRef.current = true;
    const trimmed = title.trim();
    if (!shouldSave || !trimmed) {
      onDone();
      return;
    }
    const { data } = await createTask(workflowId, {
      refetchQueries: [
        { query: Apollo.GetTaskWithCommentsDocument, variables: { taskId: parentTaskId } },
      ],
      variables: {
        input: { props: { description: '', parentTaskId, phaseId, title: trimmed, workflowId } },
      },
    });
    if (data?.createTask.task) onDone();
    else isSettledRef.current = false;
  };

  return (
    <div className={classes.row}>
      <SubtaskIcon isComplete={false} />
      <InputBase
        autoFocus
        className={classes.draftInput}
        disabled={loading}
        inputProps={{ 'aria-label': 'Sub-task title' }}
        placeholder="Sub-task title"
        value={title}
        onBlur={() => void settle(true)}
        onChange={event => setTitle(event.target.value)}
        onKeyDown={event => {
          if (event.key === 'Enter') void settle(true);
          if (event.key === 'Escape') void settle(false);
        }}
      />
    </div>
  );
}
